import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { Heading, SecondaryText } from 'components/core/Typography';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import Button from 'components/Button/Button';
import { PropagateLoader } from 'react-spinners';
import moment, { Moment } from 'moment';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AutomatedCheckModal_Run_MutationResponse } from '../__generated__/AutomatedCheckModal_Run_Mutation.graphql';
import ErrorItem from './ErrorItem';
import {
  faCircleExclamation,
  faCircleExclamationCheck
} from '@fortawesome/pro-solid-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  divider: {
    width: '100%',
    height: 1,
    background: '#a4a4a442',
    margin: '30px 0px'
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'flex-end'
  },
  loadingContainer: {
    height: 70,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  loadingText: {},
  loader: {
    height: 30
  },
  checkItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  alert: {
    display: 'flex',
    alignItems: 'center',
    padding: '20px 12px',
    background: '#faf8f6',
    borderRadius: 8,
    border: '1px solid #f5eeeb',
    marginBottom: 30
  },
  alertTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#a14136'
  },
  alertDescription: {
    fontSize: 13
  },
  alertInfo: {
    marginLeft: 20
  },
  alertIconContainer: {
    minWidth: 55,
    minHeight: 55,
    display: 'flex',
    background: '#e45e4f',
    alignItems: 'center',
    borderRadius: 50,
    /* padding: 22px; */
    paddingLeft: 5,
    border: '5px solid #ffe4e1',
    justifyContent: 'center'
  }
}));

type ErrorItem = {
  success: Boolean;
  errors: string[];
};

type Props = {
  isLoading: boolean;
  errorData?: AutomatedCheckModal_Run_MutationResponse;
  onNext: () => void;
};

function CheckResult(props: Props) {
  const classes = useStyles();

  const [startTime, setStartTime] = React.useState<Moment>(moment());
  const [currentTime, setCurrentTime] = React.useState<Moment>(moment());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const uploadStages = [
    {
      stage: 'Checking DBS document',
      time: 28
    },
    {
      stage: 'Checking reference dates',
      time: 22
    },
    {
      stage: 'Checking ID documents',
      time: 19
    },
    {
      stage: 'Checking application documents',
      time: 10
    },
    {
      stage: 'Checking the documents for validity',
      time: 4
    },
    {
      stage: 'Uploading documents',
      time: 0
    }
  ];

  const uploadStage = uploadStages.find((stage) => {
    return moment().diff(startTime, 'seconds') >= stage.time;
  })?.stage;

  let title = 'Loading your results';

  if (!props.isLoading && props.errorData) {
    if (props.errorData.runAutoVerify.success) {
      title = 'Check complete';

      if (props.errorData.runAutoVerify.shouldSendToHuman) {
        title = 'Success - Check sent for extra verification';
      }
    } else {
      title = 'Check failed';
    }
  }
  return (
    <>
      <Heading>{title}</Heading>
      <SecondaryText>
        {props.isLoading &&
          'Checking the uploaded documents for validity, this can take up to 2 minutes. Please do not close this window.'}
        {props.errorData &&
          !props.errorData.runAutoVerify.success &&
          "Unfortunately, we couldn't verify your documents. Please fix the errors below and re-upload your check."}
        {!props.isLoading &&
          !props.errorData &&
          'An error occured while trying to verify your documents, you have not been charged - Please try again'}
      </SecondaryText>

      <div className={classes.divider} />
      {props.isLoading && (
        <div className={classes.loadingContainer}>
          <div className={classes.loader}>
            <PropagateLoader
              color={'#737988'}
              loading={props.isLoading}
              size={7}
            />
          </div>
          <SecondaryText style={{ textAlign: 'center' }}>
            {Math.floor(moment().diff(startTime, 'minutes'))}:
            {(moment().diff(startTime, 'seconds') % 60)
              .toString()
              .padStart(2, '0')}
            <br />
            {uploadStage}
          </SecondaryText>
        </div>
      )}
      {!props.isLoading && props.errorData?.runAutoVerify.shouldSendToHuman && (
        <div className={classes.alert}>
          <div className={classes.alertIconContainer}>
            <FontAwesomeIcon icon={faCircleExclamationCheck} color="white" />
          </div>

          <div className={classes.alertInfo}>
            <div className={classes.alertTitle}>
              {props.errorData?.runAutoVerify.success
                ? 'Sent for extra verification'
                : 'Will require extra checking'}
            </div>
            <div className={classes.alertDescription}>
              {props.errorData?.runAutoVerify.success
                ? "This check has been sent for extra verification, you don't need to do anything - this may take up to a week."
                : 'Please repeat this check, once other issues are fixed it shall require extra verification.'}
            </div>
          </div>
        </div>
      )}
      {!props.isLoading && props.errorData && (
        <div className={classes.checkItems}>
          <ErrorItem
            name="Application"
            errors={[...props.errorData.runAutoVerify.application.errors]}
          />
          <ErrorItem
            name="References"
            errors={[...props.errorData.runAutoVerify.reference.errors]}
          />
          <ErrorItem
            name="Interview"
            errors={[...props.errorData.runAutoVerify.interview.errors]}
          />
          <ErrorItem
            name="ID"
            errors={[...props.errorData.runAutoVerify.ident.errors]}
          />
          <ErrorItem
            name="DBS"
            errors={[...props.errorData.runAutoVerify.dbs.errors]}
          />
        </div>
      )}
      <div className={classes.divider} />
      {/* <ErrorText style={{ textAlign: 'right' }}>{errorText}</ErrorText> */}
      <Spacer vertical spacing={1} />
      <div className={classes.buttons}>
        <Button
          archetype="flatPrimary"
          disabled={props.isLoading}
          onClick={() => {
            props.onNext();
          }}
        >
          Finish
        </Button>
      </div>
    </>
  );
}

export default CheckResult;
